import { IonAlert, IonButton, IonText, IonToggle, useIonRouter } from '@ionic/react';
import { createSelector } from '@reduxjs/toolkit';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { t } from 'ttag';

import KioskLayout from '@components/KioskLayout';
import TabLayout from '@components/TabLayout';
import RoutePath from '@custom-types/RoutePath';
import { useCheckActionModal } from '@hooks/useCheckActionModal';
import { usePrintBadgeModal } from '@hooks/usePrintBadgeModal';
import { usePrinter } from '@hooks/usePrinter';
import { useScanner } from '@hooks/useScanner';
import { CheckAction, CheckEntity, CheckType } from '@pages/Check/Enums';
import Analytics from '@pages/Check/Statistics';
import { KioskMode } from '@pages/Kiosk/service';
import PeopleList from '@pages/People/List';
import { PeopleProfileMode } from '@pages/People/types';
import { generateRoute } from '@services/navigation-service';
import { getEventInfo } from '@state/event';
import { getKioskEnabled, getKioskMode } from '@state/kiosk';
import { getSessionInfo } from '@state/session';
import { CapacitorBarcodeScannerCameraDirection } from '~capacitor-plugins/barcode-scanner';

import useStatistics from './queries';

type WrapperProps = {
  kioskEnabled: boolean;
  entityName: string;
  children: ReactElement;
};
type Props = { type: CheckType };

const PrinterSettingsPanel = styled.div`
  display: flex;
  flex-direction: row;
  padding: 10px 20px;
  background-color: var(--ion-background-color, #fff);

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const ScanButton = styled(IonButton)`
  margin: 20px;
`;

const selector = createSelector(
  getEventInfo,
  getSessionInfo,
  getKioskMode,
  getKioskEnabled,
  ({ id: eventId, name: eventName }, session, kioskMode, kioskEnabled) => ({
    eventId,
    eventName,
    sessionId: session?.id,
    sessionName: session?.name,
    kioskMode,
    kioskEnabled,
  })
);

const Wrapper = ({ kioskEnabled, entityName, children }: WrapperProps) => {
  if (kioskEnabled) {
    return (
      <KioskLayout wrapContent={false} disableBack>
        {children}
      </KioskLayout>
    );
  }
  return <TabLayout title={entityName}>{children}</TabLayout>;
};

const Check = ({ type }: Props) => {
  const { push: pushRoute } = useIonRouter();

  const { action: actionParam } = useParams<{ action?: CheckAction }>();
  const { enabled: badgePrintingEnabled } = usePrinter();

  const { eventId, eventName, sessionId, sessionName, kioskMode, kioskEnabled } =
    useSelector(selector);

  const [error, setError] = useState<{ header: string; message: string }>();

  const entityId = sessionId ?? eventId;
  const entityName = sessionId ? sessionName! : eventName;
  const entityType = sessionId ? CheckEntity.Session : CheckEntity.Event;

  const { loading, checkInCount, checkOutCount, peopleCount, refetch } = useStatistics({
    eventId,
    sessionId,
    entityType,
  });

  const checkActionProps = {
    title: entityType === CheckEntity.Session ? sessionName! : eventName,
    entityType,
    entityId,
    ...(kioskEnabled && !sessionId
      ? {}
      : {
          // When the modal closes, refetch the stats
          onDismiss: refetch,
        }),
  };

  const isCheckIn =
    actionParam === CheckAction.CheckIn ||
    kioskMode === KioskMode.EventCheckIn ||
    kioskMode === KioskMode.SessionCheckIn ||
    kioskMode === KioskMode.EventCheckInAndBadgePrinting;

  const { show: showCheckModal } = useCheckActionModal({
    action: isCheckIn ? CheckAction.CheckIn : CheckAction.CheckOut,
    goBack: kioskEnabled && !sessionId,
    ...checkActionProps,
  });
  const [showPrintBadgeModal] = usePrintBadgeModal({});

  const { needsSetup } = usePrinter();
  const [enableBadgePrinting, setEnableBadgePrinting] = useState(
    entityType === CheckEntity.Event && !needsSetup
  );
  const shouldShowPrinterSettings =
    badgePrintingEnabled &&
    !kioskEnabled &&
    actionParam === CheckAction.CheckIn &&
    entityType === CheckEntity.Event &&
    type === CheckType.Scanner;

  const shouldPrintBadge =
    enableBadgePrinting &&
    (kioskMode === KioskMode.BadgePrinting ||
      (isCheckIn && (!kioskMode || kioskMode === KioskMode.EventCheckInAndBadgePrinting)));

  const onScanned = useCallback(
    (checkInCode: string | undefined) => {
      if (!checkInCode) return;
      if (shouldPrintBadge) {
        if (kioskEnabled) {
          showPrintBadgeModal({ checkInCode, shouldCheckIn: isCheckIn });
        } else {
          pushRoute(
            generateRoute(RoutePath.PrintBadgeFlow, {
              eventId,
              checkInCode,
              shouldCheckIn: isCheckIn,
            })
          );
        }
        return;
      }
      showCheckModal(checkInCode);
    },
    [
      eventId,
      isCheckIn,
      kioskEnabled,
      pushRoute,
      shouldPrintBadge,
      showCheckModal,
      showPrintBadgeModal,
    ]
  );

  const { scan } = useScanner({
    cameraDirection: kioskEnabled
      ? CapacitorBarcodeScannerCameraDirection.FRONT
      : CapacitorBarcodeScannerCameraDirection.BACK,
    onScanned,
  });

  useEffect(() => {
    if (entityType === CheckEntity.Event && !needsSetup) {
      setEnableBadgePrinting(true);
    }
  }, [entityType, needsSetup]);

  const navigateToPrinterSettings = () => {
    pushRoute(generateRoute(RoutePath.PrinterSettings, { eventId }));
  };

  const handleToggleBadgePrinting = (e: CustomEvent) => {
    setEnableBadgePrinting(e.detail.checked);
    if (needsSetup) {
      navigateToPrinterSettings();
    }
  };

  return (
    <Wrapper kioskEnabled={kioskEnabled} entityName={entityName}>
      <>
        {badgePrintingEnabled && type === CheckType.Scanner && <div style={{ flex: 1 }} />}
        {type === CheckType.Scanner && actionParam && (
          <Analytics
            showLoader={loading}
            showPeopleCount={!sessionId}
            isSession={!!sessionId}
            isCheckIn={actionParam === CheckAction.CheckIn}
            checkInCount={checkInCount}
            checkOutCount={checkOutCount}
            peopleCount={peopleCount}
          />
        )}
        {type !== CheckType.Scanner && <PeopleList mode={PeopleProfileMode.Check} />}

        {type === CheckType.Scanner && <div style={{ flex: 1 }} />}
        {type === CheckType.Scanner && <ScanButton onClick={scan}>{t`Scan`}</ScanButton>}
        <PrinterSettingsPanel>
          {shouldShowPrinterSettings && (
            <>
              <IonToggle
                labelPlacement="end"
                justify="start"
                checked={enableBadgePrinting}
                onIonChange={handleToggleBadgePrinting}
              >
                {t`Turn on Badge Printing`}
                <br />
                <IonText
                  className="ion-text-wrap"
                  color="medium"
                >{t`By enabling this feature, a badge will be printed for every scanned attendee`}</IonText>
              </IonToggle>
              <div style={{ flex: 1 }} />
              <IonButton
                disabled={!enableBadgePrinting}
                fill="outline"
                onClick={navigateToPrinterSettings}
              >{t`Printer Settings`}</IonButton>
            </>
          )}
        </PrinterSettingsPanel>
        <IonAlert
          isOpen={!!error}
          // skipcq: JS-0417
          onDidDismiss={() => {
            setError(undefined);
          }}
          header={error?.header}
          message={error?.message}
          buttons={[t`OK`]}
        />
      </>
    </Wrapper>
  );
};

export default Check;
